import { useTranslation } from 'react-i18next'

import {
  LightModeSemanticColors,
  Stack,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaClockRegular } from '@enechain/ecloud-icons'

import { iconSize } from '~/constants/iconSize'

type Props = {
  /** 約定結果が未着の札の件数 (AC-PPP-019)。0 以下なら何も描画しない。 */
  pendingBidCount: number
}

/**
 * スポットの約定結果が未着の札がある計画行の info 色 clock アイコン
 * (AC-PPP-019、design `plan/procurement.html` の `status-warn-icon--spot-pending`)。
 * tooltip は見出しと本文の 2 行。提出は止まらない案内なので warning 色にはしない。
 *
 * 件数 0 の判定は BFF が返した値をそのまま使う (frontend で追加判定しない)。
 * click は一覧行の遷移へ伝播させない (`GcExcludedKomaMark` と同じ pattern)。
 */
export const AwaitingMatchResultMark = ({
  pendingBidCount,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()

  if (pendingBidCount <= 0) {
    return null
  }

  const title = t('page:planSubmission.awaitingMatchResult.title')

  return (
    <Tooltip
      content={
        <Stack gap={1}>
          <Text textStyle="captionStrong">{title}</Text>
          <Text textStyle="caption">
            {t('page:planSubmission.awaitingMatchResult.body', {
              pendingCount: pendingBidCount,
            })}
          </Text>
        </Stack>
      }
    >
      {/* アイコンだけの要素なので aria-label を付ける。native button なのは
          tooltip を focus でも出すため (GcExcludedKomaMark と同じ)。 */}
      <button
        aria-label={title}
        onClick={(e) => {
          e.stopPropagation()
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'help',
          display: 'inline-flex',
          marginRight: '4px',
          padding: 0,
          verticalAlign: 'middle',
        }}
        type="button"
      >
        <FaClockRegular
          boxSize={iconSize.gridInline}
          color={LightModeSemanticColors.Text.infoHigh.value}
          aria-hidden="true"
        />
      </button>
    </Tooltip>
  )
}
